import React, { useRef, useState } from "react";
import Select from "react-select";
import { useNavigate, useNavigationType } from "react-router-dom";

import backend from "../../backend";
import WaitAsync from "../../components/WaitAsync";
import useTitle from "../../hooks/useTitle";
import { formatInt, formatPrice } from "../../utils";

// ─── estilos del select para el tema oscuro ───────────────────────────────────
const selectStyles = {
  option:   s => ({ ...s, color: "black" }),
  control:  s => ({ ...s, flexDirection: "row", background: "#161616", borderColor: "#333", minHeight: 34 }),
  input:    s => ({ ...s, flexDirection: "row", color: "#ddd" }),
  singleValue: s => ({ ...s, color: "#ddd" }),
  placeholder: s => ({ ...s, color: "#999" }),
  menu:     s => ({ ...s, background: "#1e1e1e", border: "1px solid #333" }),
};

// F5.8 — Orígenes de trabajos manuales (los que NO vienen de cotización ni OC formal)
const ORIGIN_OPTIONS = [
  { value: "OC",      label: "Con OC del cliente"        },
  { value: "verbal",  label: "Pedido verbal del cliente" },
  { value: "sinOC",   label: "Cliente sin OC formal"     },
  { value: "interno", label: "Interno Binimeliz"          },
];

const getNewItem = () => ({ piece: null, quantity: 1, price: "0" });

// ─── estilos base ─────────────────────────────────────────────────────────────
const S = {
  wrap: {
    // clamp: 22px lateral en desktop, ~10px en móvil (360-430px)
    display: "block", padding: "clamp(12px, 2.5vw, 18px) clamp(10px, 3vw, 22px)", background: "#1a1a1a",
    minHeight: "100%", color: "#ddd", fontFamily: "Arial,sans-serif",
    fontSize: 13, textAlign: "left", boxSizing: "border-box", width: "100%",
  },
  card: {
    background: "#242424", border: "1px solid #2e2e2e",
    borderRadius: 8, padding: "16px 18px", marginBottom: 14,
  },
  label: {
    fontSize: 10, fontWeight: 700, color: "#999",
    textTransform: "uppercase", letterSpacing: ".05em", marginBottom: 4,
  },
  input: {
    background: "#161616", border: "1px solid #333", borderRadius: 5,
    padding: "7px 10px", fontSize: 12, color: "#ddd",
    fontFamily: "Arial,sans-serif", width: "100%", boxSizing: "border-box",
  },
  sectionTitle: {
    fontSize: 10, fontWeight: 700, color: "#e30613",
    textTransform: "uppercase", letterSpacing: ".07em",
    margin: "14px 0 8px",
  },
  row: {
    display: "flex", flexDirection: "row", alignItems: "center",
    justifyContent: "space-between", padding: "5px 0",
    borderBottom: "1px solid #252525", fontSize: 12,
  },
};

// ─── componente principal ─────────────────────────────────────────────────────
const WorkOrderForm = () => {
  useTitle("Nueva OT");
  const navigate = useNavigate();
  const navigationType = useNavigationType();
  const waitRef = useRef();

  const [clients, setClients] = useState([]);
  const [pieces,  setPieces]  = useState([]);

  const [client,      setClient]      = useState(null);
  const [origin,      setOrigin]      = useState(null);
  const [clientOC,    setClientOC]    = useState("");
  const [name,        setName]        = useState("");
  const [items,       setItems]       = useState([getNewItem()]);
  const [observations,setObservations]= useState("");
  const [scheduleGantt, setScheduleGantt] = useState(true); // programar en Gantt por defecto
  const [error,       setError]       = useState();
  const [saving,      setSaving]      = useState(false);

  const getData = async () => {
    try {
      const [pRes, eRes] = await Promise.all([
        backend.get("piece"),
        backend.get("entity"),
      ]);
      setPieces(pRes.data.map(v => ({
        ...v, value: v._id,
        label: `${v.sku ? v.sku + " - " : ""}${v.name}`,
      })));
      setClients(eRes.data
        .filter(v => v.type === "client")
        .map(v => ({
          ...v, value: v._id,
          label: `${v.rut ? v.rut + " - " : ""}${v.name}`,
        })));
    } catch(e) { console.error(e); }
  };

  const cancel = () =>
    navigationType === "POP"
      ? navigate("/workOrders", { replace: true })
      : navigate(-1);

  const total = items.reduce(
    (s, it) => s + +formatInt(it.price) * (Number(it.quantity) || 0), 0
  );

  const submit = async () => {
    setError();
    setSaving(true);
    try {
      if (!name)   throw new Error("Ingresa una descripción del trabajo");
      if (!client) throw new Error("Selecciona un cliente");
      if (!origin) throw new Error("Selecciona el origen de la OT");
      if (origin?.value === "OC" && !clientOC) throw new Error("Ingresa el N° de OC del cliente");
      if (!items.length) throw new Error("Agrega al menos una pieza");
      for (const it of items) {
        if (!it.piece) throw new Error("Selecciona una pieza en cada fila");
      }
      const newWO = (await backend.post("workOrder", {
        name,
        client: client.value,
        origin: origin.value,
        clientOC: clientOC || undefined,
        pieces: items.map(it => ({
          piece: it.piece.value,
          quantity: Number(it.quantity),
          price: +formatInt(it.price),
        })),
        total,
        observations: observations || undefined,
        schedule: scheduleGantt,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      })).data;
      navigate("/workOrders", { replace: true, state: { new: newWO } });
    } catch(e) {
      setError(e?.message);
    }
    setSaving(false);
  };

  const addItem = () => setItems(p => [...p, getNewItem()]);
  const removeItem = i => setItems(p => p.filter((_, idx) => idx !== i));
  const setItemField = (i, key, val) =>
    setItems(p => { const n = [...p]; n[i] = { ...n[i], [key]: val }; return n; });

  return (
    <WaitAsync func={getData} ref={waitRef}>
      <div style={S.wrap}>

        {/* ── Header ── */}
        <div style={{ display: "flex", flexDirection: "row", alignItems: "center", justifyContent: "space-between", marginBottom: 18 }}>
          <div>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#fff" }}>Nueva OT</div>
            <div style={{ fontSize: 11, color: "#999", marginTop: 2 }}>Orden de trabajo manual</div>
          </div>
          <button onClick={cancel} style={{
            background: "transparent", color: "#cbd5e1", border: "1px solid #444",
            borderRadius: 6, padding: "6px 14px", fontSize: 12, cursor: "pointer",
          }}>← Volver</button>
        </div>

        {/* ── Nota info ── */}
        <div style={{
          background: "#001a2a", borderLeft: "3px solid #3498db",
          padding: "8px 12px", fontSize: 11, color: "#7ec8e3",
          borderRadius: "0 5px 5px 0", marginBottom: 14, lineHeight: 1.5,
        }}>
          Para trabajos que llegan sin cotización previa. Si viene de cotización aprobada, usa <strong>Convertir a OT</strong> desde la cotización.
        </div>

        {/* ── Formulario ── */}
        <div style={S.card}>

          {/* fila 1: cliente + origen (flexWrap: en móvil bajan de línea) */}
          <div style={{ display: "flex", flexDirection: "row", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 2, minWidth: 200 }}>
              <div style={S.label}>Cliente</div>
              <Select
                styles={selectStyles}
                isClearable
                onChange={setClient}
                value={client}
                options={clients}
                placeholder="Seleccionar cliente"
                classNamePrefix="react-select"
              />
            </div>
            <div style={{ flex: 1, minWidth: 150 }}>
              <div style={S.label}>Origen</div>
              <Select
                styles={selectStyles}
                isClearable
                onChange={v => { setOrigin(v); if (v?.value !== "OC") setClientOC(""); }}
                value={origin}
                options={ORIGIN_OPTIONS}
                placeholder="Origen"
                classNamePrefix="react-select"
              />
            </div>
            {origin?.value === "OC" && (
              <div style={{ flex: 1, minWidth: 150 }}>
                <div style={S.label}>N° OC del cliente</div>
                <input
                  value={clientOC}
                  onChange={e => setClientOC(e.target.value)}
                  placeholder="Ej: OC-45243"
                  style={S.input}
                />
              </div>
            )}
          </div>

          {/* descripción */}
          <div style={{ marginBottom: 12 }}>
            <div style={S.label}>Descripción del trabajo</div>
            <input
              value={name}
              onChange={e => setName(e.target.value)}
              placeholder="Ej: Mordazas 480 x40 según plano 3222-3439-20"
              style={S.input}
            />
          </div>

          {/* piezas */}
          <div style={S.sectionTitle}>Piezas</div>

          {/* items */}
          {items.map((item, i) => (
            <div key={i} style={{
              display: "flex", flexDirection: "row", gap: 8,
              alignItems: "center", marginBottom: 8,
              flexWrap: "wrap", // móvil: el selector de pieza baja de línea
            }}>
              <input
                type="number" min={1} value={item.quantity}
                onChange={e => setItemField(i, "quantity", e.target.value || 1)}
                style={{ ...S.input, width: 65, textAlign: "center" }}
              />
              <div style={{ flex: 1, minWidth: 170 }}>
                <Select
                  styles={selectStyles}
                  isClearable
                  onChange={v => setItems(p => {
                    const n = [...p];
                    n[i] = { ...n[i], piece: v, price: v?.price != null ? formatPrice(v.price) : "0" };
                    return n;
                  })}
                  value={item.piece}
                  options={pieces}
                  placeholder="Seleccionar pieza"
                  classNamePrefix="react-select"
                />
              </div>
              <div style={{ width: 130 }}>
                <input
                  value={`$${formatPrice(+formatInt(item.price))}`}
                  onChange={e => setItemField(i, "price", formatInt(e.target.value.replace(/\D/g,"")))}
                  placeholder="$ Precio unit."
                  style={{ ...S.input, textAlign: "right" }}
                />
              </div>
              <button onClick={() => removeItem(i)} style={{
                width: 30, height: 30, border: "none", background: "#333",
                color: "#cbd5e1", borderRadius: 5, cursor: "pointer", flexShrink: 0,
              }}>×</button>
            </div>
          ))}

          <button onClick={addItem} style={{
            width: "100%", background: "#2a2a2a", color: "#cbd5e1",
            border: "1px dashed #3a3a3a", borderRadius: 5,
            padding: "7px", fontSize: 12, cursor: "pointer", marginBottom: 14,
          }}>
            <i className="bi bi-plus-lg" style={{ marginRight: 6 }} />
            Agregar pieza
          </button>

          {/* resumen */}
          {items.some(it => it.piece) && (
            <>
              <div style={S.sectionTitle}>Resumen</div>
              {items.filter(it => it.piece).map((it, i) => (
                <div key={i} style={{ ...S.row, borderBottom: "1px solid #252525" }}>
                  <span style={{ color: "#bbb" }}>{it.quantity} × {it.piece.label}</span>
                  <span style={{ color: "#2ecc71", fontWeight: 700 }}>
                    ${formatPrice(+formatInt(it.price) * Number(it.quantity))}
                  </span>
                </div>
              ))}
              <div style={{ textAlign: "right", fontSize: 14, fontWeight: 700, color: "#fff", marginTop: 8 }}>
                Total <span style={{ color: "#2ecc71" }}>${formatPrice(total)}</span>
              </div>
            </>
          )}

          {/* F5.8 — Observación / Motivo del registro manual.
              Importante cuando origen no es OC formal: explica por qué este
              trabajo entra al taller sin pasar por cotización ni OC del cliente. */}
          <div style={{ marginTop: 14 }}>
            <div style={S.label}>
              {origin?.value && origin.value !== "OC"
                ? "Motivo / Observaciones (importante)"
                : "Observaciones para producción"}
            </div>
            <textarea
              value={observations}
              onChange={e => setObservations(e.target.value)}
              placeholder={
                origin?.value === "interno"
                  ? "Trabajo interno: ¿qué se está fabricando y para qué? (ej: 'matriz nueva para mordaza 480, reemplaza matriz desgastada')"
                  : origin?.value === "verbal"
                    ? "Cliente pidió por verbal/email: anota qué pidió y por qué (ej: 'urgente para mantención del 5/may, OC formal llega después')"
                    : origin?.value === "sinOC"
                      ? "Cliente trabaja sin OC formal: anota el motivo y referencia (ej: 'cliente histórico, cuenta corriente, contacto Juan Pérez')"
                      : "Instrucciones especiales, procesos externos..."
              }
              rows={3}
              style={{
                ...S.input,
                resize: "vertical",
                borderColor: origin?.value && origin.value !== "OC" && !observations.trim()
                  ? "#f39c12"
                  : "#333",
              }}
            />
            {origin?.value && origin.value !== "OC" && !observations.trim() && (
              <div style={{ fontSize: 10, color: "#f39c12", marginTop: 4 }}>
                ⚠ Recomendado completar: este trabajo entra sin OC ni cotización previa.
              </div>
            )}
          </div>

          {/* toggle carta gantt */}
          <div style={{
            display:"flex", flexDirection:"row", alignItems:"center",
            gap:10, padding:"10px 12px", background:"#1e1e1e",
            borderRadius:6, border:"1px solid #2e2e2e", marginTop:6,
          }}>
            <div
              onClick={() => setScheduleGantt(v => !v)}
              style={{
                width:36, height:20, borderRadius:10, cursor:"pointer",
                background: scheduleGantt ? "#e30613" : "#333",
                position:"relative", transition:"background .2s", flexShrink:0,
              }}
            >
              <div style={{
                position:"absolute", top:2,
                left: scheduleGantt ? 18 : 2,
                width:16, height:16, borderRadius:"50%",
                background:"#fff", transition:"left .2s",
              }} />
            </div>
            <div>
              <div style={{ fontSize:12, color:"#ddd", fontWeight:600 }}>Programar en Carta Gantt</div>
              <div style={{ fontSize:11, color: "#999" }}>
                {scheduleGantt
                  ? "Se asignarán los procesos de cada pieza a máquinas automáticamente"
                  : "La OT entra al sistema pero no se programa en el Gantt todavía"}
              </div>
            </div>
          </div>

          {error && (
            <div style={{ color: "#e74c3c", fontSize: 12, textAlign: "center", margin: "8px 0" }}>
              {error}
            </div>
          )}

          {/* botones */}
          <div style={{ display: "flex", flexDirection: "row", gap: 8, justifyContent: "flex-end", marginTop: 14 }}>
            <button onClick={cancel} style={{
              background: "transparent", color: "#cbd5e1", border: "1px solid #444",
              borderRadius: 6, padding: "7px 14px", fontSize: 12, cursor: "pointer",
            }}>Cancelar</button>
            <button onClick={submit} disabled={saving} style={{
              background: "#e30613", color: "#fff", border: "none",
              borderRadius: 6, padding: "7px 18px", fontSize: 12,
              fontWeight: 700, cursor: "pointer", opacity: saving ? 0.7 : 1,
            }}>{saving ? "Creando…" : "Crear OT + Imprimir"}</button>
          </div>

        </div>
      </div>
    </WaitAsync>
  );
};

export default WorkOrderForm;
